import React, { useMemo, useState } from 'react';

import { PhoebeChatOrganizationScopeContext } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import { dynamicWidgetBindingProps } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_binding_dom';
import { DynamicWidgetIssueNotice } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_issue_notice';
import { getDynamicWidgetMetricTags } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import {
    type CaregiverListWidgetModel,
    type CaregiverListWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { DynamicWidgetPromptActionContext } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widgets_context';
import {
    TEMPLATE_CARD_CLASS,
    TEMPLATE_CARD_END_CLASS,
    TEMPLATE_CARD_FOOTER_ACTIONS_CLASS,
    TEMPLATE_CARD_FOOTER_CLASS,
    TEMPLATE_CARD_HEADER_CLASS,
    TEMPLATE_CARD_ROW_CLASS,
    TEMPLATE_CARD_SUBTITLE_CLASS,
    TEMPLATE_CARD_TITLE_CLASS,
    TEMPLATE_OUTLINE_BUTTON_SM_CLASS,
    TEMPLATE_PRIMARY_BUTTON_SM_CLASS,
    TEMPLATE_RANK_CHIP_CLASS,
    TEMPLATE_STATUS_CLASS,
    TEMPLATE_TAG_CLASS,
    TEMPLATE_TAG_POSITIVE_CLASS,
    TEMPLATE_TAG_WARNING_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { Badge } from '@apps/web/components/ui/badge';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { StatusBadge } from '@apps/web/components/ui/status_badge';
import { UserAvatar } from '@apps/web/components/ui/user_avatar';
import { formatCaregiverName } from '@apps/web/lib/caregiver_name';
import { cn } from '@apps/web/lib/utils';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

const RANKED_PAGE_SIZE = 5;

// "missing_required_role:companion" → "Missing required role: companion",
// matching the mockup's amber caution badges.
function humanizeWarning(warning: string): string {
    const [head, detail] = warning.split(':', 2);
    const words = (head ?? warning).replace(/_/g, ' ');
    const sentence = words.charAt(0).toUpperCase() + words.slice(1);
    return detail ? `${sentence}: ${detail.replace(/_/g, ' ')}` : sentence;
}

function rankedRowMeta(row: CaregiverListWidgetRow): string {
    const parts: Array<string> = [];
    if (row.distanceMiles !== null) {
        parts.push(`${row.distanceMiles.toFixed(1)} mi`);
    }
    if (row.weeklyHours !== null && row.desiredWeeklyHours !== null) {
        parts.push(`${row.weeklyHours}/${row.desiredWeeklyHours} hrs`);
    } else if (row.weeklyHours !== null) {
        parts.push(`${row.weeklyHours} hrs this wk`);
    }
    parts.push(...row.roles.map((role) => role.toUpperCase()));
    if (row.city !== null) {
        parts.push(row.city);
    }
    return parts.join(' · ');
}

function RankedMatchBadges({ row }: { readonly row: CaregiverListWidgetRow }) {
    return (
        <div className="mt-1 flex flex-wrap gap-1">
            {row.previousVisits !== null && row.previousVisits > 0 ? (
                <span className={TEMPLATE_TAG_POSITIVE_CLASS}>
                    Worked with this client{' '}
                    {row.previousVisits === 1
                        ? 'once'
                        : `${row.previousVisits}×`}
                </span>
            ) : null}
            {row.available === true ? (
                <span className={TEMPLATE_TAG_POSITIVE_CLASS}>Available</span>
            ) : null}
            {row.preferred ? (
                <span className={TEMPLATE_TAG_CLASS}>Preferred</span>
            ) : null}
            {row.warnings.map((warning) => (
                <span key={warning} className={TEMPLATE_TAG_WARNING_CLASS}>
                    {humanizeWarning(warning)}
                </span>
            ))}
        </div>
    );
}

export function CaregiverListWidget({
    model,
    loadError = false,
    onCaregiverClick,
}: {
    readonly model: CaregiverListWidgetModel;
    readonly loadError?: boolean;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const organizationScope = React.useContext(
        PhoebeChatOrganizationScopeContext,
    );
    const promptAction = React.useContext(DynamicWidgetPromptActionContext);
    const { data: organization } = useOrganization(
        organizationScope?.organizationId ?? null,
        { reason: 'dynamic-agent-ui-retrieve-caregivers' },
    );
    const displayLastFirst =
        organization?.general_settings?.display_caregiver_names_last_first ??
        false;
    const ranked = model.variant === 'ranked';
    const [visibleRankedCount, setVisibleRankedCount] =
        useState(RANKED_PAGE_SIZE);
    const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const orderedRows = useMemo(
        () =>
            model.rows.filter(
                (row) =>
                    row.caregiverId === null ||
                    !removedIds.has(row.caregiverId),
            ),
        [model.rows, removedIds],
    );
    const visibleRows = ranked
        ? orderedRows.slice(0, visibleRankedCount)
        : orderedRows;
    const selectableIds = useMemo(
        () =>
            visibleRows.flatMap((row) =>
                row.valid && row.active !== false && row.caregiverId !== null
                    ? [row.caregiverId]
                    : [],
            ),
        [visibleRows],
    );
    const [deselectedIds, setDeselectedIds] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const selectedIds = useMemo(
        () => selectableIds.filter((id) => !deselectedIds.has(id)),
        [deselectedIds, selectableIds],
    );
    const metricTags = useMemo(
        () => getDynamicWidgetMetricTags(model),
        [model],
    );
    const isPreview = model.rowProvenance === 'event_preview';
    const hiddenRankedCount = ranked
        ? Math.max(orderedRows.length - visibleRankedCount, 0)
        : 0;

    if (model.rowCount === 0) {
        return (
            <div className={cn(TEMPLATE_CARD_CLASS, 'p-3.5')}>
                <p className="text-sm font-medium">No caregivers found</p>
            </div>
        );
    }

    const selectedRows = visibleRows.filter(
        (row) =>
            row.caregiverId !== null && selectedIds.includes(row.caregiverId),
    );

    const requestStartOutreach = () => {
        if (promptAction === null || selectedRows.length === 0) {
            return;
        }
        const names = selectedRows.map((row) =>
            formatCaregiverName(
                row.displayName ?? 'this caregiver',
                displayLastFirst,
            ),
        );
        void promptAction(
            `Start an outreach for this shift contacting ${names.join(', ')}.`,
        );
    };

    const requestMessageSelected = () => {
        if (promptAction === null || selectedRows.length === 0) {
            return;
        }
        const names = selectedRows.map((row) =>
            formatCaregiverName(
                row.displayName ?? 'this caregiver',
                displayLastFirst,
            ),
        );
        void promptAction(
            `Draft a message to ${names.join(', ')} about this shift and show it to me before sending.`,
        );
    };

    return (
        <div className={TEMPLATE_CARD_CLASS}>
            <div className={TEMPLATE_CARD_HEADER_CLASS}>
                <div className="min-w-0">
                    <p className={TEMPLATE_CARD_TITLE_CLASS}>
                        {ranked ? 'Top caregiver matches' : 'Caregivers'}
                    </p>
                    <p
                        className={TEMPLATE_CARD_SUBTITLE_CLASS}
                        {...dynamicWidgetBindingProps(
                            model.widgetId,
                            'rowCount',
                        )}
                    >
                        {ranked
                            ? `${model.rowCount} scored · showing best ${visibleRows.length} · ranked by fit`
                            : `${model.rowCount} ${
                                  model.rowCount === 1
                                      ? 'caregiver'
                                      : 'caregivers'
                              }`}
                    </p>
                </div>
            </div>
            {model.issues.map((issue) => (
                <div key={issue.id} className="px-3.5 pt-2.5">
                    <DynamicWidgetIssueNotice
                        issue={issue}
                        metricTags={metricTags}
                    />
                </div>
            ))}
            {visibleRows.map((row, rowIndex) => {
                const displayName = formatCaregiverName(
                    row.displayName ?? 'Caregiver unavailable',
                    displayLastFirst,
                );
                const caregiverId = row.caregiverId;
                const canSelect =
                    row.valid && row.active !== false && caregiverId !== null;
                const selected =
                    caregiverId !== null && selectedIds.includes(caregiverId);
                const directoryMeta = [
                    ...row.roles.map((role) => role.toUpperCase()),
                    row.office,
                    row.city,
                ].filter((value): value is string => value !== null);
                const meta = ranked
                    ? rankedRowMeta(row)
                    : directoryMeta.join(' · ');
                return (
                    <div
                        key={caregiverId ?? `caregiver-row-${rowIndex}`}
                        className={cn(
                            TEMPLATE_CARD_ROW_CLASS,
                            ranked && !selected && 'opacity-60',
                        )}
                    >
                        {ranked ? (
                            <span
                                aria-label={`Rank ${row.rank ?? rowIndex + 1}`}
                                className={TEMPLATE_RANK_CHIP_CLASS}
                            >
                                {row.rank ?? rowIndex + 1}
                            </span>
                        ) : (
                            <UserAvatar
                                displayName={displayName}
                                colorKey={caregiverId ?? displayName}
                                className="size-7"
                            />
                        )}
                        <div className="min-w-0 flex-[1_1_14rem]">
                            {caregiverId !== null &&
                            onCaregiverClick !== undefined ? (
                                <button
                                    type="button"
                                    className="hover:text-primary font-medium underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
                                    onClick={() =>
                                        onCaregiverClick(caregiverId)
                                    }
                                >
                                    {displayName}
                                </button>
                            ) : (
                                <p className="font-medium">{displayName}</p>
                            )}
                            {meta.length > 0 ? (
                                <p className="dark:text-muted-foreground truncate text-xs text-[#617578]">
                                    {meta}
                                </p>
                            ) : null}
                            {ranked ? (
                                <RankedMatchBadges row={row} />
                            ) : row.qualificationTags.length > 0 ? (
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {row.qualificationTags.map((tag) => (
                                        <Badge
                                            key={tag}
                                            variant="outline"
                                            className="font-normal"
                                        >
                                            {tag}
                                        </Badge>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                        <div className={TEMPLATE_CARD_END_CLASS}>
                            {!ranked && row.active !== null ? (
                                <StatusBadge
                                    className={
                                        TEMPLATE_STATUS_CLASS[
                                            row.active ? 'positive' : 'neutral'
                                        ]
                                    }
                                    status={{
                                        label: row.active
                                            ? 'Active'
                                            : 'Inactive',
                                        intent: row.active
                                            ? 'positive'
                                            : 'neutral',
                                    }}
                                />
                            ) : null}
                            {ranked ? (
                                <Checkbox
                                    className="size-5"
                                    aria-label={`Select ${displayName}`}
                                    checked={selected}
                                    disabled={!canSelect}
                                    onCheckedChange={(checked) => {
                                        if (caregiverId === null) {
                                            return;
                                        }
                                        setDeselectedIds((current) => {
                                            const next = new Set(current);
                                            if (checked === true) {
                                                next.delete(caregiverId);
                                            } else {
                                                next.add(caregiverId);
                                            }
                                            return next;
                                        });
                                    }}
                                />
                            ) : null}
                            {caregiverId !== null &&
                            onCaregiverClick !== undefined ? (
                                <button
                                    type="button"
                                    className={TEMPLATE_OUTLINE_BUTTON_SM_CLASS}
                                    onClick={() =>
                                        onCaregiverClick(caregiverId)
                                    }
                                >
                                    Profile
                                </button>
                            ) : null}
                            {ranked && caregiverId !== null ? (
                                <button
                                    type="button"
                                    aria-label={`Remove ${displayName}`}
                                    className="dark:text-muted-foreground flex size-6 cursor-pointer items-center justify-center rounded-md text-[15px] leading-none text-[#829494] transition-[background-color,color,transform] duration-150 hover:bg-[#FFF0F2] hover:text-[#A8002E] active:scale-[0.96] dark:hover:bg-rose-950/50 dark:hover:text-rose-300"
                                    title="Remove and don’t suggest again"
                                    onClick={() =>
                                        setRemovedIds((current) => {
                                            const next = new Set(current);
                                            next.add(caregiverId);
                                            return next;
                                        })
                                    }
                                >
                                    ×
                                </button>
                            ) : null}
                        </div>
                    </div>
                );
            })}
            <div className={TEMPLATE_CARD_FOOTER_CLASS}>
                {ranked ? (
                    <p className="text-muted-foreground text-xs">
                        {selectedIds.length} of {visibleRows.length} selected
                    </p>
                ) : null}
                {ranked && hiddenRankedCount > 0 ? (
                    <button
                        type="button"
                        className="text-foreground cursor-pointer text-xs font-medium"
                        onClick={() =>
                            setVisibleRankedCount(
                                (current) => current + RANKED_PAGE_SIZE,
                            )
                        }
                    >
                        Show {Math.min(hiddenRankedCount, RANKED_PAGE_SIZE)}{' '}
                        more
                    </button>
                ) : null}
                {isPreview ? (
                    <p className="text-muted-foreground text-xs">
                        {loadError
                            ? 'Preview only — full caregiver results are unavailable.'
                            : `Loading ${model.rowCount} rows…`}
                    </p>
                ) : null}
                {ranked && promptAction !== null ? (
                    <div className={TEMPLATE_CARD_FOOTER_ACTIONS_CLASS}>
                        <button
                            type="button"
                            className={TEMPLATE_OUTLINE_BUTTON_SM_CLASS}
                            disabled={selectedRows.length === 0}
                            onClick={requestMessageSelected}
                        >
                            Message selected
                        </button>
                        <button
                            type="button"
                            className={TEMPLATE_PRIMARY_BUTTON_SM_CLASS}
                            disabled={selectedRows.length === 0}
                            onClick={requestStartOutreach}
                        >
                            Start outreach
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
